<template>
  <!--
    PoolaRun — the run composition (FE plan Section 1). Replaces the P1-A stub. Renders
    the run header + suppliers filter + (active) balance pill + the offers list
    with infinite load-more, and opens the claim sheet on a card's claim intent.
    A CONTENT FRAGMENT — never its own <f7-page> (the host owns the page shell).

    phase: "scheduled" → locked preview (claim CTAs disabled behind the start
    countdown); "active" → the buy surface; "ended" → greyed, read-only.

    Offers + facets + claim come from usePoolaOffers(dealId); the affordability
    figures (active only) come from useWallets() and are re-fed from a claim's
    meta.balances afterwards (no extra wallet read).
  -->
  <div ref="runRootRef" class="poola-run" :data-phase="phase">
    <!-- «موسم البولة» festival skin — a live-run-window-only treatment (P2-A).
         P2-B feeds its live surfaces into the header's slots, each gated on a REAL
         meta.festival field (no fabrication — the slot is only provided when the
         backend value is present). -->
    <poola-run-header
      :phase="phase"
      :deal="deal"
      :server-offset="serverOffset"
      :poolshare-balance-cents="availablePoolshareCents"
      :festival="phase === 'active'"
      :festival-meta="festivalMeta"
    >
      <template v-if="showThermometer" #thermometer>
        <poola-festival-thermometer
          :amount-cents="collectivePoolshareCents"
        ></poola-festival-thermometer>
      </template>
      <template v-if="showSavings" #savings>
        <poola-festival-savings :amount-cents="mySeasonSavingsCents"></poola-festival-savings>
      </template>
      <template v-if="showLivePill" #live-pill>
        <poola-festival-live-pill :count="liveClaimingCount"></poola-festival-live-pill>
      </template>
    </poola-run-header>

    <!-- COUNTDOWN WEEK ONLY — «عروض البولة بتكبر»: members' live PoolShare total and
         the توفير of the visible offers against the run's goal (design approved
         1 Nov 2026). Hides itself when the backend does not send
         offersSavingsCents, or when no goal is set for the run. -->
    <poola-growing-card v-if="showGrowing" :deal="deal"></poola-growing-card>

    <!-- THE OPENING MOMENT — a one-time «موسم البولة فتح دلوقتي» reveal + a SINGLE
         confetti burst, shown only when THIS active run is the one we observed
         flip SCHEDULED→ACTIVE (community-open, never a member's own claim). -->
    <poola-festival-opening v-if="showOpening" @done="onOpeningDone"></poola-festival-opening>

    <!-- In-content 2-segment window (العروض | حجوزاتي) — mirrors the SHIPPED
         PoolaSupplierRun pattern (D5). Rendered in CONTENT, NOT a
         <f7-subnavbar>, so the shared PoolaPage navbar/shell stays untouched.
         Always visible (even during load/empty/ended), so a member can switch
         to their holdings in any phase. -->
    <div class="poola-run__segmented">
      <f7-segmented strong>
        <f7-button :active="activeSegment === 'offers'" @click="switchSegment('offers')">{{
          t('poola.buy.segments.live')
        }}</f7-button>
        <f7-button :active="activeSegment === 'mine'" @click="switchSegment('mine')">{{
          t('poola.buy.segments.mine')
        }}</f7-button>
      </f7-segmented>
    </div>

    <!-- ── العروض: the buy surface — chrome + gate + list + claim path all
         UNCHANGED from P5, only wrapped so حجوزاتي can take over the content. ── -->
    <template v-if="activeSegment === 'offers'">
      <!-- The last finished Poola, COMPACT, above the upcoming offers so old and
           new products never sit side by side. Scheduled phase + offers loaded
           and not empty only (an empty run shows the full card under its empty
           text instead). -->
      <poola-last-run-card
        v-if="showLastRunCompact"
        compact
        :run="lastRun"
        :see-all-href="lastRunHref"
      ></poola-last-run-card>

      <!-- Suppliers rail — only worth showing once ≥1 real supplier chip exists
           alongside «الكل» (client-side facet, D8). -->
      <poola-supplier-filter
        v-if="showFilter"
        :suppliers="suppliers"
        :active-supplier="activeSupplier"
        @set-supplier="setSupplier"
      ></poola-supplier-filter>

      <poola-balance-pill
        v-if="phase === 'active'"
        :cash-cents="availableCashCents"
        :poolshare-cents="availablePoolshareCents"
      ></poola-balance-pill>

      <!-- List states. Gating uses offersAll (the full loaded set) so a supplier
           filter can never strand us in a skeleton/empty. -->
      <poola-error v-if="error && !offersAll.length" @retry="loadInitial"></poola-error>

      <poola-skeleton v-else-if="loading && !offersAll.length"></poola-skeleton>

      <template v-else-if="!offersAll.length">
        <div class="poola-run__empty">
          <p class="poola-run__empty-title">{{ t('poola.buy.empty.header') }}</p>
          <p class="poola-run__empty-body">{{ t('poola.buy.empty.body') }}</p>
        </div>

        <!-- No upcoming offers yet: the full recap card under the empty text. -->
        <poola-last-run-card
          v-if="showLastRun"
          :run="lastRun"
          :see-all-href="lastRunHref"
        ></poola-last-run-card>
      </template>

      <template v-else>
        <!-- Plain container (NOT f7-list): PoolaOfferCard is a self-contained
             f7-card, mirroring PostCard's container — cards are not list items.
             Offers are grouped into completion bands, nearest-to-finish first;
             a band renders its header only when it holds ≥1 card. The order is
             FROZEN per load (usePoolaOffers.sections) so a live stock patch
             updates counts in place without reshuffling cards mid-scroll. -->
        <div class="poola-run__list">
          <template v-for="section in offerSections" :key="section.key">
            <template v-if="section.offers.length">
              <p v-if="sectionHeaderKey(section.key)" class="poola-run__band">
                {{ t(sectionHeaderKey(section.key)) }}
              </p>
              <poola-offer-card
                v-for="offer in section.offers"
                :key="offer.id"
                :offer="offer"
                :phase="phase"
                :cap-unlock="capUnlock"
                :cap-unlock-pending="capUnlockPending"
                @claim="onCardClaim"
              ></poola-offer-card>
            </template>
          </template>
        </div>

        <!-- Infinite load-more sentinel (IntersectionObserver) + a more-spinner.
             Light payload → plain list, no virtualization (FE plan Section 7). -->
        <div ref="sentinelRef" class="poola-run__sentinel" aria-hidden="true" />
        <div v-if="isLoadingMore" class="poola-run__more">
          <f7-preloader></f7-preloader>
        </div>
      </template>
    </template>

    <!-- ── حجوزاتي: the member's own claims, CROSS-RUN (from /poola-purchases,
         not this run's offers). Read-completed cards, own error/skeleton gate,
         own cursor load-more. Empty is a valid state (never hidden). ── -->
    <template v-else>
      <poola-error v-if="claimsError && !claims.length" @retry="loadClaims"></poola-error>

      <poola-skeleton
        v-else-if="(claimsLoading || !claimsLoaded) && !claims.length"
      ></poola-skeleton>

      <div v-else-if="claims.length" class="poola-run__list">
        <poola-claim-card v-for="claim in claims" :key="claim.id" :claim="claim"></poola-claim-card>

        <!-- Cross-run infinite load-more sentinel + spinner. -->
        <div ref="claimsSentinelRef" class="poola-run__sentinel" aria-hidden="true" />
        <div v-if="claimsLoadingMore" class="poola-run__more">
          <f7-preloader></f7-preloader>
        </div>
      </div>

      <!-- Empty حجوزاتي — DO NOT hide the section; "no claims yet" is a valid
           read-completed state (the member holds nothing in ANY run yet). The
           body is phase-aware: the active "go claim now 👇" nudge only makes
           sense while the run is live; scheduled (always empty — claiming is
           locked) and ended use the phase-neutral bodyIdle instead. -->
      <div v-else class="poola-run__mine-empty">
        <p class="poola-run__empty-title">{{ t('poola.buy.mine.empty.header') }}</p>
        <p class="poola-run__empty-body">
          {{
            phase === 'active' ? t('poola.buy.mine.empty.body') : t('poola.buy.mine.empty.bodyIdle')
          }}
        </p>
      </div>
    </template>

    <!-- Claim sheet (stub at P3-A; real flow lands in P5). Opened by a card's
         claim intent; fed the offer + deal + live balances so it can bound qty.
         deal-not-active flips the run under us → ask the host to refetch. -->
    <poola-claim-sheet
      v-model:opened="sheetOpened"
      :offer="activeOffer"
      :deal="deal"
      :balances="liveBalances"
      @claimed="onClaimed"
      @deal-not-active="onDealNotActive"
      @refresh-offers="loadInitial"
    ></poola-claim-sheet>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import PoolaBalancePill from './PoolaBalancePill.vue';
import PoolaClaimCard from './PoolaClaimCard.vue';
import PoolaClaimSheet from './PoolaClaimSheet.vue';
import PoolaError from './PoolaError.vue';
import PoolaFestivalLivePill from './PoolaFestivalLivePill.vue';
import PoolaFestivalOpening from './PoolaFestivalOpening.vue';
import PoolaFestivalSavings from './PoolaFestivalSavings.vue';
import PoolaFestivalThermometer from './PoolaFestivalThermometer.vue';
import PoolaGrowingCard from './PoolaGrowingCard.vue';
import PoolaLastRunCard from './PoolaLastRunCard.vue';
import PoolaOfferCard from './PoolaOfferCard.vue';
import PoolaRunHeader from './PoolaRunHeader.vue';
import PoolaSkeleton from './PoolaSkeleton.vue';
import PoolaSupplierFilter from './PoolaSupplierFilter.vue';
import { useCountdown } from '@/composables/useCountdown.js';
import { usePoolaClaims } from '@/composables/usePoolaClaims.js';
import { usePoolaOffers } from '@/composables/usePoolaOffers.js';
import { usePoolaRealtime } from '@/composables/usePoolaRealtime.js';
import { useWallets } from '@/composables/wallet/useWallets.js';
import { POOLA_GROWING_GOAL_CENTS } from '@/constants/poola.js';

const props = defineProps({
  phase: {
    type: String,
    default: 'active',
    validator: (v) => ['scheduled', 'active', 'ended'].includes(v),
  },
  // The deserialised poola-deals resource (id + flattened attributes).
  deal: {
    type: Object,
    default: null,
  },
  // serverNow − clientNow (ms) from usePoolaDeal — passed through to the header
  // countdown so a skewed phone clock doesn't flip the run early/late.
  serverOffset: {
    type: Number,
    default: 0,
  },
  // meta.festival aggregates from usePoolaDeal (real backend counters). null on
  // non-festival runs or before the BE ships the fields — every festival surface
  // feature-detects its own field and hides when absent (NO fabrication).
  festivalMeta: {
    type: Object,
    default: null,
  },
  // usePoolaDeal.openedDealId — the id of the run OBSERVED to flip
  // SCHEDULED→ACTIVE this session. When it matches this active run's id, the
  // one-time «موسم البولة فتح دلوقتي» reveal fires once (community-open only).
  openedDealId: {
    type: [String, Number],
    default: null,
  },
  // Real windowed "claiming now" member count (P3-A realtime rolling window of
  // .PoolaOfferClaimed). null = no source yet → the live pill hides. Never faked.
  claimingNowCount: {
    type: Number,
    default: null,
  },
  // usePoolaDeal.previousRun — the last finished run, passed by PoolaPage only on
  // the COMING branch. null (the default) leaves every screen exactly as before.
  lastRun: {
    type: Object,
    default: null,
  },
  // Where the recap card's link goes (the last-Poola page). null → no link.
  lastRunHref: {
    type: String,
    default: null,
  },
});

// deal-not-active bubbles to the host so it can refetch the singleton (the run
// flipped under us — the host owns usePoolaDeal). totals-changed forwards a
// `.PoolaDealTotalsChanged` realtime signal up so the host (usePoolaDeal, which
// owns meta.festival) can silently refetch fresh collective/savings aggregates.
const emit = defineEmits(['deal-not-active', 'totals-changed']);

const { t } = useI18n({ useScope: 'global' });

// filter[deal] is required (a blank id 422s); a getter keeps it reactive if the
// deal id ever changes under us.
const dealId = computed(() => props.deal?.id ?? null);

const {
  sections: offerSections,
  offersAll,
  suppliers,
  activeSupplier,
  setSupplier,
  hasMore,
  isLoadingMore,
  loading,
  error,
  loadInitial,
  loadMore,
  applyClaimBroadcast,
  resort,
  refetchForCapIncrease,
} = usePoolaOffers(dealId);

// Band → i18n header key. 'regular' (the un-banded middle group) has no header.
const SECTION_HEADER_KEYS = {
  nearComplete: 'poola.buy.sections.nearComplete',
  completed: 'poola.buy.sections.completed',
};
function sectionHeaderKey(key) {
  return SECTION_HEADER_KEYS[key] ?? null;
}

// ── Cap-unlock countdown (P2-A) ──────────────────────────────────────────────
// ONE deal-wide clock ticking to the scheduled per-member cap lift
// (`deal.capIncreaseAt`), shared by every offer card. Derived locally from the
// `deal` + `serverOffset` props already flowing in — this keeps PoolaPage's nine
// PoolaRun call-sites untouched (cleaner than threading a new prop through each).
// PoolaRun is only mounted behind a resolved deal (PoolaPage v-else-if on state)
// and is re-keyed per phase, so the deal — and thus this target — is stable for
// the instance's lifetime; reading it once at setup is safe.

/** ISO string | epoch-ms | null → epoch ms | null (NaN-safe; mirrors usePoolaDeal.toMs). */
function toMs(value) {
  if (value == null) return null;
  const ms = typeof value === 'number' ? value : Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

// Offset-adjusted cap-unlock target (built exactly like usePoolaDeal's
// start/end/capIncreaseAtAdjusted, so a skewed device clock can't fire it
// early/late). null when no bump is scheduled.
const capIncreaseAtAdjusted = computed(() => {
  const ms = toMs(props.deal?.capIncreaseAt);
  return ms == null ? null : ms - props.serverOffset;
});

// "A lift is scheduled AND still in the future for this LIVE run" — a plain
// boolean read once at setup (deal/phase don't change under a keyed, deal-gated
// PoolaRun). Passed to every card so the per-card predicate can gate on the
// deal-wide fact. Active phase only (scheduled/ended never show a cap countdown).
const capUnlockPending =
  props.phase === 'active' &&
  capIncreaseAtAdjusted.value != null &&
  capIncreaseAtAdjusted.value > Date.now();

// Instantiate the clock ONLY when a lift is genuinely pending — a null/past
// target would make useCountdown flag isPast immediately and wrongly trip the
// refetch. When not pending, cards receive a null `capUnlock` and their
// predicate stays inert. useCountdown owns its own interval + visibilitychange
// catch-up + teardown (device-slept-past-boundary resumes → update() flips
// isPast → the watcher below fires the one-time refetch).
const capUnlock = capUnlockPending ? useCountdown(capIncreaseAtAdjusted.value) : null;

// On elapse, do the authoritative refetch ONCE (the locked "client countdown →
// refetch" contract — no push, no client-side cap mutation). Guarded against a
// re-fire from any later isPast churn.
let capRefetchFired = false;
if (capUnlock) {
  watch(
    () => capUnlock.isPast,
    (isPast) => {
      if (!isPast || capRefetchFired) return;
      capRefetchFired = true;
      refetchForCapIncrease();
    },
  );
}

// «حجوزاتي» is the member's CROSS-RUN claims history — sourced from the dedicated
// /poola-purchases endpoint, NOT derived from this run's offers (the old
// myClaimedQty derivation silently dropped claims on unloaded pages and in any
// past/ended run). Loaded lazily on the first switch to the segment.
const {
  claims,
  hasMore: claimsHasMore,
  isLoadingMore: claimsLoadingMore,
  loading: claimsLoading,
  error: claimsError,
  loaded: claimsLoaded,
  loadInitial: loadClaims,
  loadMore: loadMoreClaims,
  ensureLoaded: ensureClaimsLoaded,
} = usePoolaClaims();

// ── Segment state (العروض | حجوزاتي) ─────────────────────────────────────────
// 'offers' = the buy surface; 'mine' = the member's claimed offers. A server-
// confirmed claim patches myClaimedQty on the offer, so myClaims (and thus the
// حجوزاتي panel) re-derives with no extra fetch when the member switches over.
const activeSegment = ref('offers'); // 'offers' | 'mine'

function switchSegment(seg) {
  if (activeSegment.value === seg) return;
  activeSegment.value = seg;
  // First open of «حجوزاتي» → fetch the cross-run claims (no-op once loaded).
  if (seg === 'mine') ensureClaimsLoaded();
  // Returning to «العروض» → re-sort the bands. This is the AC2 "segment-switch"
  // resort point and a SAFE moment to reflow: the offers list was unmounted while
  // on «حجوزاتي», so it remounts scrolled to the top — no card jumps under the
  // thumb — while offers that completed live (frozen under a stale band) settle
  // into «اكتملوا». Realtime patches still never resort; only this explicit switch.
  if (seg === 'offers') resort();
  if ('vibrate' in navigator) navigator.vibrate(10);
}

// ── Affordability (active only) ──────────────────────────────────────────────
const { mainWallet, poolShareWallet, refresh: refreshWallets } = useWallets();

// A claim's meta.balances is authoritative once it lands — prefer it over the
// initial wallet read (meta.balances shape: { defaultAvailableCents, poolshareCents }).
const claimBalances = ref(null);

const availableCashCents = computed(
  () =>
    claimBalances.value?.defaultAvailableCents ?? mainWallet.value?.availableBalanceCents ?? null,
);
const availablePoolshareCents = computed(
  () => claimBalances.value?.poolshareCents ?? poolShareWallet.value?.availableBalanceCents ?? null,
);

// Bundle for the claim sheet's maxQty math.
const liveBalances = computed(() => ({
  cashCents: availableCashCents.value,
  poolshareCents: availablePoolshareCents.value,
}));

// Show the rail only when there's a real supplier chip beyond «الكل».
const showFilter = computed(() => suppliers.value.length > 1);

// ── Last-Poola recap card (countdown screen only) ────────────────────────────
// Scheduled phase + a previous run handed down by the host. The compact variant
// rides above a non-empty list; the empty branch renders the full variant itself.
// Loading or a first-load error with no offers renders neither.
const showLastRun = computed(() => props.phase === 'scheduled' && props.lastRun != null);
// The growing card needs the BE field (feature-detect, no fabrication) and a goal.
const showGrowing = computed(
  () =>
    props.phase === 'scheduled' &&
    props.deal?.offersSavingsCents != null &&
    POOLA_GROWING_GOAL_CENTS > 0,
);
const showLastRunCompact = computed(() => showLastRun.value && offersAll.value.length > 0);

// ── Festival live surfaces (P2-B) ────────────────────────────────────────────
// Every surface is fed a REAL meta.festival field and only rendered when that
// field is present (feature-detect → hide-when-absent, never a fabricated value).
// All are active-phase only — the festival skin is a live-run-window treatment.
const festivalActive = computed(() => props.phase === 'active');

// The club's COLLECTIVE PoolShare "profit till now": the sum of PoolShare used by
// ALL نادي السيلرز members across the whole season (cumulative, NOT today, NOT
// cash). This is a NEW backend aggregate (meta.festival.collectivePoolshareUsedCents)
// requested via FE→BE brief 2026-07-10; until BE ships it the field is absent →
// the computed stays null → the thermometer hides (feature-detect, never faked).
// Deliberately does NOT fall back to the old cash figure — cash mislabelled as
// PoolShare profit would be a dishonest number.
const collectivePoolshareCents = computed(
  () => props.festivalMeta?.collectivePoolshareUsedCents ?? null,
);
const mySeasonSavingsCents = computed(() => props.festivalMeta?.mySeasonSavingsCents ?? null);

// ── "Claiming now" rolling window (realtime, P3-A) ───────────────────────────
// There is NO polled source for a live "right now" window on meta.festival — so
// the live pill is fed a REAL count derived from a rolling window of
// `.PoolaOfferClaimed` events (client receive-time, so it decays on the user's
// clock). Never fabricated: absent a socket the window stays empty → the pill
// hides. A parent-supplied claimingNowCount prop (e.g. tests) overrides it.
const CLAIMING_WINDOW_MS = 60_000; // "claiming now" = genuine claims in the last minute
const CLAIMING_PRUNE_MS = 10_000; // decay cadence so an idle window shrinks to 0
const claimTimes = ref([]);
let pruneTimer = null;

function pruneClaimWindow() {
  const cutoff = Date.now() - CLAIMING_WINDOW_MS;
  const kept = claimTimes.value.filter((t) => t >= cutoff);
  if (kept.length !== claimTimes.value.length) claimTimes.value = kept;
}

const realtimeClaimingNow = computed(() => claimTimes.value.length);
const liveClaimingCount = computed(() =>
  props.claimingNowCount != null ? props.claimingNowCount : realtimeClaimingNow.value,
);

// Only once the club has actually accumulated collective PoolShare (> 0) — a live
// ticker, not a hollow «0» at the opening moment (mirrors the savings gate below).
const showThermometer = computed(
  () =>
    festivalActive.value &&
    collectivePoolshareCents.value != null &&
    collectivePoolshareCents.value > 0,
);
// Member-only (PoolaRun is the buy path) + only once they've actually saved
// (> 0), so a fresh member never sees a hollow «وفّرت 0».
const showSavings = computed(
  () =>
    festivalActive.value && mySeasonSavingsCents.value != null && mySeasonSavingsCents.value > 0,
);
// Fed by the realtime rolling window (or a parent override) — hidden until there
// is a real positive count (never faked).
const showLivePill = computed(
  () => festivalActive.value && liveClaimingCount.value != null && liveClaimingCount.value > 0,
);

// ── Live near-complete/completed band repair ─────────────────────────────────
// usePoolaOffers.resort() doc: band membership (sections.nearComplete/regular/
// completed) is FROZEN at load and deliberately NOT rebuilt on every reactive
// change to a loaded offer — a resort mid-scroll would yank a card out from
// under the reading thumb (the P4-A stability rule; the P4-A prompt's own task
// 2 flags this as the exact regression to avoid). In PRODUCTION, verified
// against the raw /poola-offers response vs the rendered DOM, the frozen band
// goes stale from MANY sources, not just a `.PoolaOfferClaimed` frame THIS
// client happens to receive: another member's claim/refund broadcast, THIS
// member's own server-confirmed claim (claim()'s local patch), a reseed
// (loadMore/PTR landing offers already past the threshold), or simply the
// load→view gap (the offer crossed the line between fetch time and the moment
// the member actually looks). Hooking each source individually (the earlier
// onClaimEvent-only version of this fix) misses the rest.
//
// So instead: `bandSignature` fingerprints every LOADED offer's own band
// bucket (nearComplete/regular/completed, derived from the SAME isComplete/
// isNearComplete getters resort()/sortAndGroupOffers use — never re-derived
// here). A `watch` on that fingerprint catches a crossing from ANY source,
// because every source above works by mutating a loaded PoolaOffer entity
// (repo.save), which offersAll (and therefore this computed) already reacts to
// (see usePoolaOffers.js's own comments on offersAll-derived computeds
// re-deriving on a live patch).
//
// The trigger itself — a debounced resort(), fired only when the offers
// scroll container is at the TOP — is UNCHANGED from the original fix: at the
// top there is no card under a reading thumb to jump, a resort there only
// inserts/removes a band header above content the user hasn't scrolled past
// (the same "nothing to jump" argument switchSegment's segment-switch resort,
// the AC2 point above, relies on via a remount). Debounced so a burst of
// signature changes (several offers completing together) coalesces into a
// single resort instead of thrashing on every change.
const TOP_RESORT_DEBOUNCE_MS = 500;
const SCROLL_TOP_THRESHOLD_PX = 8; // "at the top" tolerance (rubber-band bounce)
let topResortTimer = null;
const runRootRef = ref(null);
// Resolved once on mount (see onMounted below) — PoolaRun is always rendered as
// a content fragment inside PoolaPage's <f7-page-content> (CLAUDE.md tabbed-
// shell notes; verified `.page-content` is the real `overflow:auto` scroll
// element, node_modules/framework7/framework7-bundle.css:383), so `.closest()`
// from our own root finds the actual scroll container without any F7 API.
let offersScrollEl = null;

function isOffersScrolledToTop() {
  // No scroll container resolved → fail CLOSED (never resort). The P4-A
  // no-jump guarantee outranks the cosmetic band repair; an unresolved
  // container must never become a silent "always resort" backdoor.
  return offersScrollEl != null && offersScrollEl.scrollTop <= SCROLL_TOP_THRESHOLD_PX;
}

function scheduleTopResort() {
  if (topResortTimer) window.clearTimeout(topResortTimer);
  topResortTimer = window.setTimeout(() => {
    topResortTimer = null;
    // Re-check at FIRE time, not schedule time — the user may have scrolled
    // in either direction during the debounce window.
    if (isOffersScrolledToTop()) resort();
  }, TOP_RESORT_DEBOUNCE_MS);
}

// One char per loaded offer's OWN band bucket — cheap to diff (a plain string
// compare on `watch`), and only changes when an offer actually crosses a
// bucket boundary (patching e.g. piecesRemaining without crossing the
// threshold leaves every letter the same, so the watcher stays silent).
const bandSignature = computed(() =>
  offersAll.value
    .map((o) => `${o.id}:${o.isComplete ? 'C' : o.isNearComplete ? 'N' : 'R'}`)
    .join('|'),
);

// Skip exactly the FIRST change (the initial load populating offersAll from
// empty) — loadInitial's own reset path already calls resort() synchronously
// for that population (usePoolaOffers.js `if (reset) resort();`), so
// scheduling a second one here would be a redundant, obvious double-resort on
// mount. Every change AFTER that first one is a real post-load mutation (from
// any of the sources above) and should schedule the debounced repair. A
// `watch` (no `immediate`) never fires for the pre-load → doesn't run at all
// until the FIRST real change, so this flag distinguishes "that first change
// IS the initial population" from "a later, genuine live change".
let sawFirstBandSignature = false;
watch(bandSignature, () => {
  if (!sawFirstBandSignature) {
    sawFirstBandSignature = true;
    return;
  }
  scheduleTopResort();
});

// ── Realtime (progressive enhancement) ───────────────────────────────────────
// PoolaRun is the live-phase host (remounts per phase via PoolaPage's :key), so
// onMounted/onUnmounted is the natural subscribe/leave lifecycle. usePoolaRealtime
// shares ONE PoolaDeal.{dealId} channel with usePoolaDeal (which owns the STATUS
// frame) via an internal refcount — our leave never tears out the sibling's
// listener. Polling (loadInitial on mount + PTR) stays the fallback for no-socket.
function onClaimEvent(event) {
  const kind = applyClaimBroadcast(event); // live stock patch (absolute, monotonic-guarded)
  // Count ONLY a confirmed new claim on a loaded offer. Exclude 'unknown' (the
  // offer isn't paginated in yet, so applyClaimBroadcast can't tell claim from
  // refund): .PoolaOfferClaimed fires on refunds too, and counting an unclassified
  // frame would let a refund inflate the honest "claiming now" number.
  if (kind === 'claim') {
    claimTimes.value = [...claimTimes.value, Date.now()];
    pruneClaimWindow();
  }
  // NOTE: no direct resort trigger here anymore — the `bandSignature` watch
  // above catches this same stock patch (applyClaimBroadcast mutates the
  // entity, offersAll/bandSignature react) generically, alongside every other
  // source that can move an offer across a completion threshold.
}

function onTotalsEvent(event) {
  // Deal-scoped channel, but filter defensively before nudging the host to refetch
  // meta.festival (usePoolaDeal owns it — the thermometer/savings live off it).
  if (event?.deal_id != null && String(event.deal_id) !== String(props.deal?.id)) return;
  emit('totals-changed');
}

function onRealtimeReconnect() {
  // Socket gap → refetch authoritative stock, and drop the now-unreliable window
  // (claims may have fired/expired while the socket was down).
  claimTimes.value = [];
  loadInitial();
}

usePoolaRealtime(dealId, {
  onClaim: onClaimEvent,
  onTotals: onTotalsEvent,
  onReconnect: onRealtimeReconnect,
});

// THE OPENING MOMENT — fire the one-time reveal only when THIS active run is the
// one usePoolaDeal observed flip SCHEDULED→ACTIVE. PoolaPage remounts PoolaRun
// with a fresh :key on the flip, so the immediate watch evaluates once on mount
// with openedDealId already set. openingFired guards against any re-fire within
// this instance; the deal-scoped id match guards against a direct landing on an
// already-active run and against a *different* run's stale opening signal.
const showOpening = ref(false);
const openingFired = ref(false);

watch(
  () => [props.openedDealId, dealId.value],
  ([openedId, id]) => {
    if (openingFired.value || !festivalActive.value) return;
    if (openedId == null || id == null || openedId !== id) return;
    openingFired.value = true;
    showOpening.value = true;
  },
  { immediate: true },
);

function onOpeningDone() {
  showOpening.value = false;
}

// ── Claim sheet wiring ───────────────────────────────────────────────────────
const sheetOpened = ref(false);
const activeOffer = ref(null);

function onCardClaim(offer) {
  // CTA gating already locks non-active phases at the card; guard here too.
  if (props.phase !== 'active') return;
  activeOffer.value = offer;
  sheetOpened.value = true;
}

function onClaimed(result) {
  // result: { purchase, balances } — swap in the authoritative post-claim
  // balances so the pill reflects the burn immediately. Do NOT close here: the
  // sheet owns its success step + dismissal (it closes via update:opened).
  if (result?.balances) claimBalances.value = result.balances;
  // The member just added a claim in this run — refresh «حجوزاتي» so it shows up
  // next time they open it. Only if already loaded; otherwise the first open
  // fetches fresh (and includes this claim) anyway.
  if (claimsLoaded.value) loadClaims();
}

function onDealNotActive() {
  sheetOpened.value = false;
  emit('deal-not-active');
}

// ── Infinite load-more (IntersectionObserver) ────────────────────────────────
const sentinelRef = ref(null);
let observer = null;

function maybeLoadMore() {
  if (hasMore.value && !loading.value && !isLoadingMore.value) loadMore();
}

function ensureObserver() {
  if (observer || typeof IntersectionObserver === 'undefined') return;
  observer = new IntersectionObserver(
    (entries) => {
      if (entries.some((e) => e.isIntersecting)) maybeLoadMore();
    },
    { rootMargin: '200px' },
  );
}

// Re-bind whenever the sentinel mounts/unmounts (it only renders with offers).
watch(sentinelRef, (el) => {
  ensureObserver();
  if (!observer) return;
  observer.disconnect();
  if (el) observer.observe(el);
});

// ── Claims load-more (own sentinel + observer; only mounted on the «حجوزاتي»
// segment with ≥1 claim) ─────────────────────────────────────────────────────
const claimsSentinelRef = ref(null);
let claimsObserver = null;

function maybeLoadMoreClaims() {
  if (claimsHasMore.value && !claimsLoading.value && !claimsLoadingMore.value) loadMoreClaims();
}

function ensureClaimsObserver() {
  if (claimsObserver || typeof IntersectionObserver === 'undefined') return;
  claimsObserver = new IntersectionObserver(
    (entries) => {
      if (entries.some((e) => e.isIntersecting)) maybeLoadMoreClaims();
    },
    { rootMargin: '200px' },
  );
}

watch(claimsSentinelRef, (el) => {
  ensureClaimsObserver();
  if (!claimsObserver) return;
  claimsObserver.disconnect();
  if (el) claimsObserver.observe(el);
});

onMounted(() => {
  loadInitial();
  // One affordability read on the live surface only (FE plan Section 7).
  if (props.phase === 'active') refreshWallets();
  // Decay the "claiming now" window even when no new claims arrive, so an idle
  // pill shrinks back to 0 (and hides) instead of freezing at its last count.
  pruneTimer = window.setInterval(pruneClaimWindow, CLAIMING_PRUNE_MS);
  // Resolve the offers scroll container once — see the block above onClaimEvent.
  offersScrollEl = runRootRef.value?.closest?.('.page-content') ?? null;
});

onUnmounted(() => {
  observer?.disconnect();
  observer = null;
  claimsObserver?.disconnect();
  claimsObserver = null;
  if (pruneTimer) {
    window.clearInterval(pruneTimer);
    pruneTimer = null;
  }
  if (topResortTimer) {
    window.clearTimeout(topResortTimer);
    topResortTimer = null;
  }
});

// Exposed for the host / tests to drive load-more without the observer.
defineExpose({ loadMore: maybeLoadMore });
</script>

<style scoped>
.poola-run {
  padding-bottom: 24px;
}

/* ── In-content segment control (العروض | حجوزاتي) ─────────────────────────── */
/* direction: ltr keeps the two pills in a stable visual order regardless of the
   page's RTL direction — matches PoolaSupplierRun. */
.poola-run__segmented {
  max-width: 400px;
  margin: 0 auto 8px;
  padding: 0 16px;
  direction: ltr;
}

.poola-run__list {
  display: flex;
  flex-direction: column;
}

/* Completion section band header — a lightweight label above its group
   («قربوا يكملوا 🔥» / «اكتملوا 🎉»). Not rendered for the un-banded middle
   group, nor for any empty band (see the template guard). */
.poola-run__band {
  color: var(--poolit-text-primary);
  font-size: var(--poolit-font-size-small);
  font-weight: var(--poolit-font-weight-semibold);
  margin: 16px 16px 4px;
}

.poola-run__mine-empty {
  text-align: center;
  padding: 48px 28px;
}

.poola-run__sentinel {
  height: 1px;
}

.poola-run__more {
  display: flex;
  justify-content: center;
  padding: 12px 0;
}

.poola-run__empty {
  text-align: center;
  padding: 48px 28px;
}

.poola-run__empty-title {
  color: var(--poolit-text-primary);
  font-size: var(--poolit-font-size-body);
  font-weight: var(--poolit-font-weight-semibold);
  margin: 0 0 8px;
}

.poola-run__empty-body {
  color: var(--poolit-text-muted);
  font-size: var(--poolit-font-size-small);
  margin: 0;
  max-width: 300px;
  margin-inline: auto;
}

/* Ended runs render greyed + read-only (locked design). */
.poola-run[data-phase='ended'] {
  opacity: 0.55;
}
</style>
